<script setup lang="ts">
import StoryCard from '@/components/customer-stories/StoryCard.vue'
import { stories } from '@/data/stories'
import { useHead } from '@unhead/vue'
import { easeOut, motion } from 'motion-v'

const route = useRoute()

const { t } = useI18n()

const storiesSection = ref<HTMLElement | null>(null)

useHead({
  link: [
    {
      rel: 'canonical',
      href: `${import.meta.env.VITE_MONOLITH_URL}/customer-stories`,
    },
  ],
})

useSeoMeta({
  title: t('customer_stories.meta.title'),
  description: t('customer_stories.meta.description'),
  ogTitle: t('customer_stories.meta.title'),
  ogDescription: t('customer_stories.meta.description'),
})

const categories = [
  'All Results',
  'Visibility',
  'Conversion',
  'Retention',
  'Automate',
]

const activeCategory = ref(
  (route.query.category as string) || 'All Results',
)

const filteredStories = computed(() => {
  if (activeCategory.value === 'All Results') {
    return stories
  }
  return stories.filter(story => story.category === activeCategory.value)
})

const storiesToShow = ref(6)
const displayedStories = computed(() => filteredStories.value.slice(0, storiesToShow.value))
const loadMore = () => {
  storiesToShow.value += 6
}

// Motion variants
const headlineVariants = {
  initial: { opacity: 0, y: '10%' },
  animate: { opacity: 1, y: 0, transition: { duration: 0.4, ease: easeOut } },
}

const storyVariants = {
  initial: { opacity: 0, y: 50 },
  animate: (custom: unknown) => {
    const i = Number(custom)
    return {
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.1, duration: 0.5, ease: easeOut },
    }
  },
}

const buttonVariants = {
  initial: { opacity: 0, scale: 0.8 },
  animate: { opacity: 1, scale: 1, transition: { duration: 0.3, ease: easeOut } },
}

const scrollToStories = async () => {
  await nextTick()
  if (storiesSection.value) {
    storiesSection.value.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

watch(
  () => route.query.category,
  async (newCategory) => {
    activeCategory.value = (newCategory as string) || 'All Results'
    await scrollToStories()
  },
)

onMounted(async () => {
  await nextTick()
  if (route.query.category) {
    scrollToStories()
  }
})
</script>

<template>
  <div class="isolate">
    <LayoutNavbar />

    <div class="mb-0 bg-gradient-to-b from-[#f8f8f0] to-[#f8f8f0] pt-10 md:shadow-2xl md:shadow-black/10">
      <!-- Headline -->
      <section class="3xl:px-0 mx-auto mb-8 px-4 lg:max-w-screen-xl">
        <motion.h1
          class="block  text-center text-3xl font-medium  !leading-tight text-slate-900 lg:text-5xl"
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
        >
          Customer Stories
        </motion.h1>
        <motion.p
          class="py-6 text-center text-slate-700"
          :initial="{ opacity: 0, y: 20 }"
          :while-in-view="{ opacity: 1, y: 0, transition: { delay: 0.2, duration: 0.4 } }"
        >
          These are real outcomes from restaurant operators who implemented our platform.
        </motion.p>
      </section>

      <!-- Categories -->
      <div class="mb-8 w-full bg-white  pb-8 pt-8 md:rounded-[5rem] ">
        <h2 class="leading-1 mx-auto w-[90%] pb-12 pt-8 text-center text-4xl font-medium text-slate-900">
          See how restaurants grow with SpotHopper
        </h2>
        <section
          ref="storiesSection"
          class="mx-auto mb-8 px-2 lg:max-w-screen-xl xl:px-0 "
        >
          <motion.div
            class="mb-10 flex flex-wrap  gap-3 px-4 xl:justify-start xl:px-0"
            :initial="{ opacity: 0, y: 20 }"
            :while-in-view="{ opacity: 1, y: 0, transition: { duration: 0.5 } }"
          >
            <button
              v-for="category in categories"
              :key="category"
              class="w-full rounded-xl px-5 py-3 text-sm font-medium transition md:w-max"
              :class="[
                activeCategory === category
                  ? 'bg-orange-500 text-white'
                  : 'bg-[#31323E14] text-slate-900 hover:bg-[#31323E25]'
              ]"
              @click="activeCategory = category"
            >
              {{ category }}
            </button>
          </motion.div>

          <!-- Story Cards -->
          <div class="grid w-full grid-cols-1 gap-x-4 gap-y-12 md:grid-cols-3 xl:gap-x-8">
            <motion.div
              v-for="(story, index) in displayedStories"
              :key="story.slug"
              :custom="index"
              :variants="storyVariants"
              initial="initial"
              while-in-view="animate"
            >
              <StoryCard v-bind="story" />
            </motion.div>
          </div>

          <!-- Load More -->
          <motion.div
            class="mt-8 flex justify-center"
            :variants="buttonVariants"
            initial="initial"
            while-in-view="animate"
          >
            <button
              v-if="storiesToShow < filteredStories.length"
              class="rounded-xl bg-orange-500 px-6 py-3 font-medium text-white transition hover:bg-orange-600"
              @click="loadMore"
            >
              Load More Articles
            </button>
          </motion.div>
        </section>

        <!-- CTA Section -->
        <section class="mx-auto px-2 pb-10 lg:max-w-screen-xl lg:pb-16 2xl:px-0">
          <div class="relative overflow-clip rounded-3xl px-4 py-10 md:px-8 md:py-20">
            <div class="absolute inset-0 isolate before:absolute before:inset-0 before:z-[1] before:bg-gray-800/70">
              <motion.img
                :initial="{ opacity: 0, y: 50 }"
                :while-in-view="{ opacity: 1, y: 0, transition: { duration: 0.5, ease: easeOut } }"
                :src="`${S3_ASSETS_URL}/home/cta-02.jpg`"
                class="aspect-video size-full object-cover object-center"
                role="presentation"
              />
            </div>

            <div class="isolate grid place-items-center gap-5">
              <motion.h3
                :variants="headlineVariants"
                initial="initial"
                while-in-view="animate"
                class="max-w-screen-sm text-balance text-center text-3xl leading-tight text-white lg:text-5xl"
              >
                Ready to take your restaurant marketing to the next level?
              </motion.h3>

              <UiButton
                class="group w-full max-w-xs lg:max-w-max"
                as-child
              >
                <RouterLink :to="{ name: '/free-demo' }">
                  Book a Free Demo

                  <UiIcon
                    name="arrow-forward-rounded"
                    class="transition-transform duration-200 group-hover:translate-x-1"
                  />
                </RouterLink>
              </UiButton>
            </div>
          </div>
        </section>
      </div>

      <LayoutFooter />
    </div>
  </div>
</template>
